import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Upload,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  PageHeaderSkeleton,
  StatCardsSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import {
  SortableTableHead,
  sortItems,
  useTableSort,
} from "~/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import {
  inventoryAdjustmentsService,
  type UnifiedAdjustment,
} from "~/services/purchasing/inventoryAdjustmentsService";
import { formatCurrency } from "~/utils/helpers";
import { litersToKg } from "~/utils/oilConversions";

import { BulkStockAdjustmentDialog } from "./components/BulkStockAdjustmentDialog";
import { StockAdjustmentDialog } from "./components/StockAdjustmentDialog";

const qty = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

const typeLabel = (t: string) =>
  t === "product"
    ? "Producto"
    : t === "oil"
      ? "Aceite"
      : t === "raw_material"
        ? "Materia Prima"
        : t;

// Oils are stored in liters but bought/counted in kg, so display them in kg
// (liters × density), matching the Historial de Movimientos. Products and raw
// materials keep their native unit.
const isOilKg = (r: UnifiedAdjustment) =>
  r.item_type === "oil" && r.density != null && r.density > 0;

const displayQuantity = (r: UnifiedAdjustment) =>
  isOilKg(r) ? litersToKg(r.quantity, r.density as number) : r.quantity;

const displayUnit = (r: UnifiedAdjustment) =>
  isOilKg(r) ? "kg" : (r.unit ?? "");

const getSortValue = (
  r: UnifiedAdjustment,
  field: string
): string | number | null => {
  switch (field) {
    case "fecha":
      return dayjs(r.fecha).valueOf();
    case "type":
      return typeLabel(r.item_type);
    case "item":
      return r.name ?? null;
    case "location":
      return r.location ?? null;
    case "quantity":
      return displayQuantity(r);
    case "cost":
      return r.unit_cost ?? null;
    case "reason":
      return r.reason ?? null;
    case "user":
      return r.user_name ?? null;
    default:
      return null;
  }
};

export function InventoryAdjustmentsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [itemType, setItemType] = useState<"all" | "product" | "raw_material" | "oil">("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["inventory-adjustments", itemType, fromDate, toDate],
    queryFn: () =>
      inventoryAdjustmentsService.getAll({
        itemType,
        from: fromDate || undefined,
        to: toDate || undefined,
      }),
  });

  const rows = useMemo(() => {
    const all = data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(term) ||
        (r.code ?? "").toLowerCase().includes(term) ||
        (r.reason ?? "").toLowerCase().includes(term)
    );
  }, [data, search]);

  const kpis = useMemo(() => {
    let inc = 0,
      dec = 0,
      prod = 0,
      mp = 0,
      oil = 0;
    for (const r of rows) {
      if (r.quantity > 0) inc++;
      else if (r.quantity < 0) dec++;
      if (r.item_type === "product") prod++;
      else if (r.item_type === "oil") oil++;
      else mp++;
    }
    return { total: rows.length, inc, dec, prod, mp, oil };
  }, [rows]);

  const { sort, toggleSort } = useTableSort({
    field: "fecha",
    direction: "desc",
  });

  const sortedRows = useMemo(
    () => sortItems(rows, sort, getSortValue),
    [rows, sort]
  );

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    sortedRows,
    20
  );

  const clearFilters = () => {
    setItemType("all");
    setFromDate("");
    setToDate("");
    setSearch("");
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <StatCardsSkeleton count={4} />
        <TableSkeleton rows={10} columns={7} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Ajustes de Inventario"
        description="Ajustes de stock de productos terminados e ingredientes (materia prima y aceites)."
        actions={
          <>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setBulkOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Ajuste Masivo
            </Button>
            <Button className="h-9" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Ajuste
            </Button>
          </>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total ajustes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.total}</p>
            <p className="text-xs text-muted-foreground">
              {kpis.prod} PT · {kpis.mp} MP · {kpis.oil} aceites
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Incrementos
            </CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-700">{kpis.inc}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Decrementos
            </CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-700">{kpis.dec}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              PT / MP / Aceites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {kpis.prod} / {kpis.mp} / {kpis.oil}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar ítem, código o razón…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
        <Select value={itemType} onValueChange={(v) => setItemType(v as typeof itemType)}>
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="product">Producto Terminado</SelectItem>
            <SelectItem value="raw_material">Materia Prima</SelectItem>
            <SelectItem value="oil">Aceite</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="h-9 w-full sm:w-[150px]"
        />
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="h-9 w-full sm:w-[150px]"
        />
        <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters}>
          Limpiar
        </Button>
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead sortKey="fecha" sort={sort} onSort={toggleSort}>
                    Fecha
                  </SortableTableHead>
                  <SortableTableHead sortKey="type" sort={sort} onSort={toggleSort}>
                    Tipo
                  </SortableTableHead>
                  <SortableTableHead sortKey="item" sort={sort} onSort={toggleSort}>
                    Ítem
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="location"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Ubicación
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="quantity"
                    sort={sort}
                    onSort={toggleSort}
                    className="text-right"
                  >
                    Cantidad
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="cost"
                    sort={sort}
                    onSort={toggleSort}
                    className="text-right"
                  >
                    Costo Unit.
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="reason"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Razón
                  </SortableTableHead>
                  <SortableTableHead sortKey="user" sort={sort} onSort={toggleSort}>
                    Usuario
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-40 p-0">
                      <EmptyState
                        icon={SlidersHorizontal}
                        title="Sin ajustes"
                        description="No hay ajustes para estos filtros."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((r, i) => (
                    <TableRow key={`${r.item_id}-${r.fecha}-${i}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {dayjs(r.fecha).format("DD/MM/YYYY HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {typeLabel(r.item_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <div className="truncate font-medium">{r.name ?? "—"}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.code}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.location ?? "—"}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold tabular-nums ${r.quantity > 0 ? "text-green-700" : r.quantity < 0 ? "text-red-700" : ""}`}
                      >
                        {r.quantity > 0 ? "+" : ""}
                        {qty(displayQuantity(r))}{" "}
                        <span className="text-xs font-normal text-muted-foreground">
                          {displayUnit(r)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.unit_cost != null ? `$${formatCurrency(r.unit_cost)}` : "—"}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-muted-foreground">
                        {r.reason ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.user_name ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {rows.length > 0 && (
            <div className="border-t p-4">
              <TablePagination
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={20}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <StockAdjustmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => refetch()}
      />

      <BulkStockAdjustmentDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onSuccess={() => refetch()}
      />
    </PageContainer>
  );
}
